import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Edit2, History } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { getAlerts, createAlert, updateAlert, deleteAlert } from '../api/client';

interface Alert {
  id: string;
  kpi: string;
  condition: '>' | '<' | '=';
  threshold: number;
  channel: 'Slack' | 'Email' | 'Webhook';
  channelConfig: string;
  status: 'active' | 'triggered' | 'muted';
  lastTriggered?: string;
  createdAt: string;
}

interface AlertHistory {
  id: string;
  alertId: string;
  triggeredAt: string;
  value: number;
  message: string;
}

type Tab = 'alerts' | 'history';

export const AlertManager: React.FC = () => {
  const { t } = useTranslation();
  const [alerts, setAlerts] = useState<Alert[]>([]);
  const [alertHistory] = useState<AlertHistory[]>([]);

  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('alerts');
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState<Pick<Alert, 'kpi' | 'condition' | 'threshold' | 'channel' | 'channelConfig'>>({
    kpi: 'ggr',
    condition: '<',
    threshold: 10000,
    channel: 'Slack',
    channelConfig: '',
  });

  const kpis = [
    { id: 'ggr', label: 'Gross Gaming Revenue' },
    { id: 'ngr', label: 'Net Gaming Revenue' },
    { id: 'turnover', label: 'Turnover' },
    { id: 'deposits', label: 'Deposits' },
    { id: 'withdrawals', label: 'Withdrawals' },
    { id: 'activePlayers', label: 'Active Players' },
  ];

  useEffect(() => {
    loadAlerts();
  }, []);

  // Mapping: backend condition → frontend symbol
  const conditionMap: Record<string, '>' | '<' | '='> = {
    greater_than: '>', less_than: '<', equals: '=',
  };
  const reverseConditionMap: Record<string, string> = {
    '>': 'greater_than', '<': 'less_than', '=': 'equals',
  };

  const mapBackendAlert = (rule: any): Alert => ({
    id: String(rule.id),
    kpi: rule.kpi,
    condition: conditionMap[rule.condition] || '<',
    threshold: rule.threshold,
    channel: (rule.channel?.charAt(0).toUpperCase() + rule.channel?.slice(1)) as any,
    channelConfig: rule.channel_config?.webhook_url || rule.channel_config?.recipient_email || rule.channel_config?.url || '',
    status: rule.is_active ? 'active' : 'muted',
    lastTriggered: rule.last_triggered_at,
    createdAt: rule.created_at?.split('T')[0] || '',
  });

  const mapToBackend = (form: typeof formData) => ({
    site_id: 1, // default site for now
    kpi: form.kpi,
    condition: reverseConditionMap[form.condition] || 'less_than',
    threshold: form.threshold,
    channel: form.channel.toLowerCase(),
    channel_config: form.channel === 'Slack'
      ? { webhook_url: form.channelConfig }
      : form.channel === 'Email'
        ? { recipient_email: form.channelConfig, smtp_host: 'localhost', smtp_port: '587', sender_email: 'alerts@betstarters.com', sender_password: 'placeholder' }
        : { url: form.channelConfig },
  });

  const loadAlerts = async () => {
    try {
      const response = await getAlerts();
      const rules = response.data || [];
      setAlerts(rules.map(mapBackendAlert));
    } catch (error) {
      console.error('Failed to load alerts:', error);
    }
  };

  const handleSaveAlert = async () => {
    setLoading(true);
    try {
      const payload = mapToBackend(formData);
      if (editingId) {
        await updateAlert(editingId, payload);
      } else {
        await createAlert(payload);
      }
      setShowModal(false);
      setEditingId(null);
      setFormData({ kpi: 'ggr', condition: '<', threshold: 10000, channel: 'Slack', channelConfig: '' });
      await loadAlerts();
    } catch (error) {
      console.error('Failed to save alert:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAlert = async (id: string) => {
    try {
      await deleteAlert(id);
      setAlerts(alerts.filter((a) => a.id !== id));
    } catch (error) {
      console.error('Failed to delete alert:', error);
    }
  };

  const handleEditAlert = (alert: Alert) => {
    setFormData({
      kpi: alert.kpi,
      condition: alert.condition,
      threshold: alert.threshold,
      channel: alert.channel,
      channelConfig: alert.channelConfig,
    });
    setEditingId(alert.id);
    setShowModal(true);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      active: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      triggered:
        'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      muted:
        'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300',
    };
    return styles[status] || '';
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">
            {t('alerts.title')}
          </h1>
          <p className="mt-1 text-slate-600 dark:text-slate-400">
            {t('alerts.subtitle')}
          </p>
        </div>
        <button
          onClick={() => {
            setEditingId(null);
            setFormData({
              kpi: 'ggr',
              condition: '<',
              threshold: 10000,
              channel: 'Slack',
              channelConfig: '',
            });
            setShowModal(true);
          }}
          className="flex items-center gap-2 rounded-lg bg-accent px-4 py-2 font-medium text-white transition-colors hover:bg-accent-600"
        >
          <Plus className="h-4 w-4" />
          {t('alerts.newAlert')}
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700">
        <button
          onClick={() => setTab('alerts')}
          className={`px-4 py-2 font-medium transition-colors ${
            tab === 'alerts'
              ? 'border-b-2 border-accent text-accent'
              : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
          }`}
        >
          {t('alerts.tabActiveAlerts')}
        </button>
        <button
          onClick={() => setTab('history')}
          className={`flex items-center gap-2 px-4 py-2 font-medium transition-colors ${
            tab === 'history'
              ? 'border-b-2 border-accent text-accent'
              : 'text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white'
          }`}
        >
          <History className="h-4 w-4" />
          {t('alerts.tabHistory')}
        </button>
      </div>

      {/* Alerts List */}
      {tab === 'alerts' && (
        <div className="grid gap-4">
          {alerts.length === 0 ? (
            <div className="rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 py-12 text-center dark:border-slate-700 dark:bg-slate-800">
              <p className="text-slate-600 dark:text-slate-400">
                {t('alerts.noAlertsConfigured')}
              </p>
            </div>
          ) : (
            alerts.map((alert) => (
              <div
                key={alert.id}
                className="flex flex-col justify-between gap-4 rounded-lg border border-slate-200 bg-white p-4 dark:border-slate-700 dark:bg-slate-800 sm:flex-row sm:items-center"
              >
                <div className="flex-1">
                  <div className="flex items-center gap-3">
                    <span className="font-medium text-slate-900 dark:text-white">
                      {kpis.find((k) => k.id === alert.kpi)?.label}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-xs font-semibold ${getStatusBadge(
                        alert.status
                      )}`}
                    >
                      {t(`alerts.status${alert.status.charAt(0).toUpperCase() + alert.status.slice(1)}`)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
                    {t('alerts.triggerWhen')} {alert.kpi}{' '}
                    {alert.condition === '>' ? t('alerts.greaterThan') : t('alerts.lessThan')}{' '}
                    {alert.threshold} {t('alerts.via')} {alert.channel}
                  </p>
                  {alert.lastTriggered && (
                    <p className="mt-1 text-xs text-slate-500 dark:text-slate-500">
                      {t('alerts.lastTriggered')} {alert.lastTriggered}
                    </p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={() => handleEditAlert(alert)}
                    className="rounded-lg border border-slate-300 bg-white p-2 text-slate-600 transition-colors hover:bg-slate-100 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-400 dark:hover:bg-slate-600"
                  >
                    <Edit2 className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => handleDeleteAlert(alert.id)}
                    className="rounded-lg border border-red-300 bg-white p-2 text-red-600 transition-colors hover:bg-red-50 dark:border-red-600/30 dark:bg-red-900/10 dark:text-red-400 dark:hover:bg-red-900/20"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* History Tab */}
      {tab === 'history' && (
        <div className="overflow-x-auto rounded-lg border border-slate-200 bg-white shadow-sm dark:border-slate-700 dark:bg-slate-800">
          <table className="w-full">
            <thead>
              <tr className="border-b border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900">
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white">
                  {t('alerts.historyColumnKpi')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white">
                  {t('alerts.historyColumnValue')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white">
                  {t('alerts.historyColumnTriggeredAt')}
                </th>
                <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900 dark:text-white">
                  {t('alerts.historyColumnMessage')}
                </th>
              </tr>
            </thead>
            <tbody>
              {alertHistory.map((history) => {
                const alert = alerts.find((a) => a.id === history.alertId);
                return (
                  <tr
                    key={history.id}
                    className="border-b border-slate-200 transition-colors hover:bg-slate-50 dark:border-slate-700 dark:hover:bg-slate-700/50"
                  >
                    <td className="px-6 py-4 font-medium text-slate-900 dark:text-white">
                      {alert?.kpi}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                      {history.value}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                      {new Date(history.triggeredAt).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 text-slate-600 dark:text-slate-400">
                      {history.message}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-md rounded-lg bg-white shadow-xl dark:bg-slate-800">
            {/* Modal Header */}
            <div className="border-b border-slate-200 px-6 py-4 dark:border-slate-700">
              <h2 className="text-lg font-bold text-slate-900 dark:text-white">
                {editingId ? t('alerts.editAlertTitle') : t('alerts.createNewAlertTitle')}
              </h2>
            </div>

            {/* Modal Body */}
            <div className="space-y-4 px-6 py-4">
              <div>
                <label className="block text-sm font-medium text-slate-900 dark:text-white">
                  {t('alerts.kpiLabel')}
                </label>
                <select
                  value={formData.kpi}
                  onChange={(e) =>
                    setFormData({ ...formData, kpi: e.target.value })
                  }
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                >
                  {kpis.map((kpi) => (
                    <option key={kpi.id} value={kpi.id}>
                      {kpi.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-900 dark:text-white">
                    {t('alerts.conditionLabel')}
                  </label>
                  <select
                    value={formData.condition}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        condition: e.target.value as '<' | '>' | '=',
                      })
                    }
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  >
                    <option value="<">{t('alerts.conditionLessThan')}</option>
                    <option value=">">{t('alerts.conditionGreaterThan')}</option>
                    <option value="=">{t('alerts.conditionEqualTo')}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-900 dark:text-white">
                    {t('alerts.thresholdLabel')}
                  </label>
                  <input
                    type="number"
                    value={formData.threshold}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        threshold: parseFloat(e.target.value),
                      })
                    }
                    className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 dark:text-white">
                  {t('alerts.channelLabel')}
                </label>
                <select
                  value={formData.channel}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      channel: e.target.value as 'Slack' | 'Email' | 'Webhook',
                    })
                  }
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                >
                  <option value="Slack">{t('alerts.channelSlack')}</option>
                  <option value="Email">{t('alerts.channelEmail')}</option>
                  <option value="Webhook">{t('alerts.channelWebhook')}</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-900 dark:text-white">
                  {formData.channel === 'Slack'
                    ? t('alerts.channelConfigSlack')
                    : formData.channel === 'Email'
                      ? t('alerts.channelConfigEmail')
                      : t('alerts.channelConfigWebhook')}
                </label>
                <input
                  type={formData.channel === 'Email' ? 'email' : 'text'}
                  value={formData.channelConfig}
                  onChange={(e) =>
                    setFormData({ ...formData, channelConfig: e.target.value })
                  }
                  placeholder={
                    formData.channel === 'Slack'
                      ? t('alerts.placeholderSlack')
                      : formData.channel === 'Email'
                        ? t('alerts.placeholderEmail')
                        : t('alerts.placeholderWebhook')
                  }
                  className="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-slate-900 dark:border-slate-600 dark:bg-slate-700 dark:text-white"
                />
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex gap-3 border-t border-slate-200 px-6 py-4 dark:border-slate-700">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 rounded-lg border border-slate-300 bg-white px-4 py-2 font-medium text-slate-900 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-700 dark:text-white dark:hover:bg-slate-600"
              >
                {t('alerts.cancel')}
              </button>
              <button
                onClick={handleSaveAlert}
                disabled={loading || !formData.channelConfig}
                className="flex-1 rounded-lg bg-accent px-4 py-2 font-medium text-white transition-colors hover:bg-accent-600 disabled:opacity-50"
              >
                {loading ? t('alerts.saving') : t('alerts.saveAlert')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
